/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Box, Flex, Table, Tabs, Text, TextField, Tooltip } from '@radix-ui/themes';
import { useCurrentStateAndParams, useRouter } from '@uirouter/react';
import { FileCheck, Search } from 'lucide-react';

import { EmptyState } from '../../../../shared/EmptyState';
import { ErrorState } from '../../../../shared/ErrorState';
import { FilterSection, FilterSidebar } from '../../../../shared/FilterSidebar';
import { LoadingState } from '../../../../shared/LoadingState';
import { SortableTableHeader } from '../../../../shared/SortableTableHeader/SortableTableHeader';
import type { SortDirection } from '../../../../shared/SortableTableHeader/SortableTableHeader';
import { TablePagination } from '../../../../shared/TablePagination/TablePagination';
import { ThreatLevelBadge } from '../../../../shared/ThreatLevelBadge';
import { NO_VALUE, scopeLabel, toDisplayDate, toIsoDate } from './waiverFormat';
import { useWaiversList } from './useWaiversList';
import type { WaiverFilterId, WaiverRow, WaiverType, WaiverView, WaiversListState } from './waiversListTypes';
import { parseWaiversUrl, serializeWaiversUrl } from './waiversListUrl';

const FILTERS: { id: WaiverFilterId; label: string }[] = [
  { id: 'status', label: 'Status' },
  { id: 'threat', label: 'Threat' },
  { id: 'policy', label: 'Policy' },
  { id: 'repository', label: 'Repository' },
];

const SORTABLE_COLUMNS: { key: string; label: string }[] = [
  { key: 'threat', label: 'Threat' },
  { key: 'dateCreated', label: 'Created' },
  { key: 'expiration', label: 'Expiration' },
  { key: 'policy', label: 'Policy' },
  { key: 'scope', label: 'Scope' },
];

const SEARCH_DEBOUNCE_MS = 300;

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function WaiverRows({ rows }: { rows: WaiverRow[] }): JSX.Element {
  return (
    <>
      {rows.map((row) => (
        <Table.Row key={row.id} data-testid={`waiver-row-${row.id}`}>
          <Table.Cell>
            <Tooltip content={`Threat level ${row.threatLevel} of 10`}>
              <span>
                <ThreatLevelBadge threatLevel={row.threatLevel} />
              </span>
            </Tooltip>
          </Table.Cell>
          <Table.Cell>
            <Text size="2">{toDisplayDate(row.createTime)}</Text>
          </Table.Cell>
          <Table.Cell>
            <Badge color={row.status === 'expired' ? 'red' : 'gray'} variant="soft" size="1">
              {row.expiryTime === null ? 'Never' : toIsoDate(row.expiryTime)}
            </Badge>
          </Table.Cell>
          <Table.Cell>
            <Text size="2">{row.policyName || NO_VALUE}</Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="2">{scopeLabel(row.scopeOwnerType, row.scopeOwnerName)}</Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="2">{row.displayName || NO_VALUE}</Text>
          </Table.Cell>
        </Table.Row>
      ))}
    </>
  );
}

export function WaiversLivePage(): JSX.Element {
  const router = useRouter();
  const { params } = useCurrentStateAndParams();
  const state = useMemo(() => parseWaiversUrl(params), [params]);
  const { data, loading, error, reload } = useWaiversList(state);
  const [search, setSearch] = useState(state.q);

  const update = useCallback(
    (patch: Partial<WaiversListState>, resetPage = true) => {
      const next = { ...state, ...patch, page: resetPage ? 1 : (patch.page ?? state.page) };
      router.stateService.go('.', serializeWaiversUrl(next), { location: 'replace' });
    },
    [router, state]
  );

  useEffect(() => setSearch(state.q), [state.q]);
  useEffect(() => {
    if (search === state.q) {
      return undefined;
    }
    const timer = setTimeout(() => update({ q: search }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, state.q, update]);

  // Container image waivers are owned by the image's application, never by a repository.
  const filters = state.type === 'containers' ? FILTERS.filter(({ id }) => id !== 'repository') : FILTERS;

  const sections: FilterSection[] = filters.map(({ id, label }) => {
    const counts = data?.counts?.[id] ?? {};
    const values = new Set([...Object.keys(counts), ...state[id]]);
    return {
      id,
      label,
      type: 'checkbox',
      defaultExpanded: true,
      value: state[id],
      options: [...values].sort().map((value) => ({
        value,
        label: id === 'status' || id === 'threat' ? capitalize(value) : value,
        count: counts[value] ?? 0,
      })),
    };
  });

  const clearFilters = () => update({ status: [], threat: [], policy: [], repository: [], q: '' });
  const requests = state.view === 'requested';
  const total = data?.total ?? 0;
  const hasFilters = filters.some(({ id }) => state[id].length > 0) || !!state.q;

  const onSort = (key: string, direction: SortDirection) =>
    update({ sort: key, dir: direction === 'asc' ? 'asc' : 'desc' });

  let body: JSX.Element;
  if (error) {
    body = <ErrorState title="Waivers unavailable" message={error} onRetry={reload} retryText="Retry" />;
  } else if (loading && !data) {
    body = <LoadingState />;
  } else if (!data || total === 0) {
    body = (
      <EmptyState
        icon={FileCheck}
        title={hasFilters ? 'No waivers match the filters' : requests ? 'No waiver requests' : 'No waivers'}
        description={
          hasFilters
            ? 'Try removing a filter or changing the search.'
            : 'Waivers for this instance, including those inherited from the organization, will appear here.'
        }
      />
    );
  } else {
    body = (
      <>
        <Table.Root variant="surface" data-testid="waivers-table">
          <Table.Header>
            <Table.Row>
              {SORTABLE_COLUMNS.map(({ key, label }) => (
                <SortableTableHeader
                  key={key}
                  sortKey={key}
                  currentSortKey={state.sort}
                  currentSortDirection={state.sort ? state.dir : null}
                  onSort={onSort}
                >
                  {label}
                </SortableTableHeader>
              ))}
              <Table.ColumnHeaderCell>{state.type === 'containers' ? 'Images' : 'Components'}</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <WaiverRows rows={data.results} />
          </Table.Body>
        </Table.Root>
        <TablePagination
          currentPage={state.page}
          totalPages={Math.max(1, Math.ceil(total / state.pageSize))}
          itemsPerPage={state.pageSize}
          totalItems={total}
          onPageChange={(page) => update({ page }, false)}
          onItemsPerPageChange={(pageSize) => update({ pageSize })}
        />
      </>
    );
  }

  return (
    <Flex direction="column" gap="3" data-testid="waivers-live-page">
      <Tabs.Root value={state.type} onValueChange={(type) => update({ type: type as WaiverType, status: [], threat: [], policy: [], repository: [], q: '', sort: null })}>
        <Tabs.List>
          <Tabs.Trigger value="components">Components</Tabs.Trigger>
          <Tabs.Trigger value="containers">Containers</Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>
      <Tabs.Root value={state.view} onValueChange={(view) => update({ view: view as WaiverView, status: [] })}>
        <Tabs.List size="1">
          <Tabs.Trigger value="existing">Existing</Tabs.Trigger>
          <Tabs.Trigger value="requested">Requested</Tabs.Trigger>
        </Tabs.List>
      </Tabs.Root>
      <Flex gap="4" align="start">
        <FilterSidebar sections={sections} onFilterChange={(id, value) => update({ [id]: Array.isArray(value) ? value : [value] })} onClear={clearFilters} disabled={!!error} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <TextField.Root
            mb="3"
            placeholder="Search waivers"
            aria-label="Search waivers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          >
            <TextField.Slot>
              <Search size={14} aria-hidden />
            </TextField.Slot>
          </TextField.Root>
          {body}
        </Box>
      </Flex>
    </Flex>
  );
}
